import { useParams } from 'react-router-dom';

import { Grid, SelectChangeEvent } from '@mui/material';
import useTheme from 'api/useTheme';
import { OfferUiProps } from 'common/contracts';
import { ESpecialOfferInternalViewModel } from 'constants/enums';

import AcCard from 'components/AcCard/AcCard';
import AcInput from 'components/AcInput/AcInput';
import AcSelect from 'components/AcSelect/AcSelect';

import { OfferDesignTypes } from '../types';

const DEFAULT_COLLECT_TEXT = 'Collect';

const OfferUiSettings: React.FC<OfferUiProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  isNameUnique,
  isExternalIdUnique,
  offerDesignTypesItems,
  setOfferUiType
}) => {
  const { publisherId } = useParams();
  const { getGeneralTheme: generalTheme } = useTheme(publisherId);

  const getMaxCollectTextLength = () => {
    const specialOffersInternalViewModel =
      generalTheme?.data?.specialOffersInternalViewModel;
    return specialOffersInternalViewModel ===
      ESpecialOfferInternalViewModel.SPIN_X ||
      specialOffersInternalViewModel ===
        ESpecialOfferInternalViewModel.CASH_FRENZY
      ? 25
      : 8;
  };

  const maxCollectTextLength = getMaxCollectTextLength();
  const handleCollectTextBlur = (e: React.FocusEvent<any>) => {
    handleBlur(e);
    const collectText = values.specialOffer?.collectText?.trim();
    if (!collectText) {
      setFieldValue('specialOffer.collectText', DEFAULT_COLLECT_TEXT);
    }
  };

  const getCollectTextHelper = () => {
    if (touched.specialOffer?.collectText && errors.specialOffer?.collectText) {
      return errors.specialOffer.collectText;
    }
    if (values.specialOffer?.collectText?.length === maxCollectTextLength) {
      return `Maximum ${maxCollectTextLength} characters reached`;
    }
    return '';
  };

  const handleOfferUiTypeChange = (e: SelectChangeEvent<unknown>) => {
    handleChange(e);
    setOfferUiType?.(e.target.value as string);
  };

  return (
    <AcCard title="Offer Settings" padded={false} className="offersUIFormCard">
      <Grid container columnSpacing={1}>
        <Grid item xs={6}>
          <AcInput
            required
            header="Name"
            name="name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            error={(touched.name && Boolean(errors.name)) || !isNameUnique}
            helperText={
              (touched.name ? errors.name : '') ||
              (!isNameUnique ? 'Name already exists' : '')
            }
          />
        </Grid>
        <Grid item xs={6}>
          <AcSelect
            header="Type"
            name="offerUiType"
            value={values.offerUiType}
            onChange={handleOfferUiTypeChange}
            onBlur={handleBlur}
            items={offerDesignTypesItems}
          />
        </Grid>
      </Grid>

      <Grid container mt={2} columnSpacing={1}>
        <Grid item xs={6}>
          <AcInput
            required
            header="External ID"
            name="externalId"
            value={values.externalId}
            onChange={handleChange}
            onBlur={handleBlur}
            error={
              (touched.externalId && Boolean(errors.externalId)) ||
              !isExternalIdUnique
            }
            helperText={
              (touched.externalId ? errors.externalId : '') ||
              (!isExternalIdUnique ? 'External ID already exists' : '')
            }
          />
        </Grid>

        {values.offerUiType === OfferDesignTypes.SpecialOffer && (
          <Grid item xs={6}>
            <AcInput
              required
              header="Free Offer Default Text"
              name="specialOffer.collectText"
              value={values.specialOffer?.collectText}
              onChange={handleChange}
              onBlur={handleCollectTextBlur}
              error={
                touched.specialOffer?.collectText &&
                Boolean(errors.specialOffer?.collectText)
              }
              helperText={getCollectTextHelper()}
              slotProps={{
                htmlInput: {
                  maxLength: maxCollectTextLength
                }
              }}
            />
          </Grid>
        )}
      </Grid>
    </AcCard>
  );
};

export default OfferUiSettings;
